import React, { useEffect, useState } from 'react';
import styles from './refund-confirm-modal.module.scss';
import { ModalWindow, DefaultButton, Alert } from 'shared/ui';
import { BusinessApi } from 'shared/api';
import { IRefundCalculation } from 'shared/api/business/interfaces/refund-calculation.interface';

interface IRefundConfirmModalProps {
  paymentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const RefundConfirmModal: React.FC<IRefundConfirmModalProps> = ({
  paymentId,
  onClose,
  onSuccess,
}) => {
  const [calculation, setCalculation] = useState<IRefundCalculation | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    BusinessApi.refundCalculate(paymentId)
      .then(setCalculation)
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : 'Ошибка расчёта возврата'
        );
      })
      .finally(() => setLoading(false));
  }, [paymentId]);

  const handleRefund = async () => {
    setRefunding(true);
    setError(null);
    try {
      await BusinessApi.refundPayment(paymentId);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка возврата');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <ModalWindow onClickOutside={() => onClose()}>
      <div className={styles.card}>
        <h2 className={styles.title}>Подтверждение возврата</h2>

        {loading ? (
          <div className={styles.loading}>Расчёт возврата...</div>
        ) : calculation ? (
          <>
            <div className={styles.details}>
              <div className={styles.row}>
                <span className={styles.label}>Оплачено</span>
                <span className={styles.value}>
                  {calculation.calculation.paidAmount} ₽
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>Тариф</span>
                <span className={styles.value}>
                  {calculation.calculation.monthlyEmployeePrice} ₽/сотр
                </span>
              </div>
              {calculation.calculation.monthSalePercent > 0 && (
                <div className={styles.row}>
                  <span className={styles.label}>Скидка</span>
                  <span className={styles.value}>
                    {calculation.calculation.monthSalePercent}%
                  </span>
                </div>
              )}
              <div className={styles.row}>
                <span className={styles.label}>Месячный тариф со скидкой</span>
                <span className={styles.value}>
                  {calculation.calculation.monthlyTotalDiscPrice} ₽
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>Дней прошло</span>
                <span className={styles.value}>
                  {calculation.calculation.daysPassed}
                </span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>
                  Стоимость использованных дней
                </span>
                <span className={styles.value}>
                  {calculation.calculation.costOfUsedDays} ₽
                </span>
              </div>
              <div className={`${styles.row} ${styles.totalRow}`}>
                <span className={styles.label}>К возврату</span>
                <span className={styles.value}>
                  {calculation.refundAmount} ₽
                </span>
              </div>
            </div>

            <div className={styles.actions}>
              <DefaultButton
                buttonText="Отмена"
                onClick={onClose}
                color="red"
                disabled={refunding}
              />
              <DefaultButton
                buttonText={`Вернуть ${calculation.refundAmount} ₽`}
                onClick={handleRefund}
                color="green"
                disabled={refunding}
              />
            </div>
          </>
        ) : null}

        {error && !loading && (
          <Alert type="error" message={error} onClose={() => setError(null)} />
        )}
      </div>
    </ModalWindow>
  );
};
